import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, CheckCircle, Loader2, XCircle, ArrowLeft } from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';

import { API_URL } from '../utils/api';
const API = API_URL;

export default function SubscriptionSuccessPage() {
  const { clubId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error, expired
  const [message, setMessage] = useState('Verifying your payment...');
  const [plan, setPlan] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('No session ID found. Please try again.');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await safeFetch(`${API}/api/stripe/subscription/status/${sessionId}`);
        
        if (!res.ok) {
          throw new Error('Failed to check status');
        }
        
        const data = res.data;
        
        if (data.status === 'success' && data.payment_status === 'paid') {
          setStatus('success');
          setMessage(data.message || 'Subscription activated!');
          setPlan(data.plan);
          setExpiresAt(data.expires_at);
        } else if (data.status === 'expired') {
          setStatus('expired');
          setMessage(data.message || 'Session expired. Please try again.');
        } else if (data.status === 'pending') {
          // Keep polling
          if (pollCount < 10) {
            setTimeout(() => {
              setPollCount(prev => prev + 1);
            }, 2000);
          } else {
            setStatus('error');
            setMessage('Payment verification timed out. Please check your email for confirmation.');
          }
        }
      } catch (error) {
        console.error('Error checking status:', error);
        setStatus('error');
        setMessage('Failed to verify payment. Please contact support.');
      }
    };

    checkStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-amber-500 animate-spin mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Verifying Payment</h1>
              <p className="text-slate-600">{message}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome to Premium!</h1>
              <p className="text-slate-600 mb-4">{message}</p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">
                    {plan === 'yearly' ? 'Yearly Plan' : 'Monthly Plan'}
                  </span>
                </div>
                {expiresAt && (
                  <p className="text-sm text-amber-700">
                    Active until: {new Date(expiresAt).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                )}
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Your premium features are now active:</p>
                <ul className="text-left text-sm text-slate-600 space-y-1 pl-4">
                  <li>✓ Featured Club placement</li>
                  <li>✓ DJ Live requests</li>
                  <li>✓ Bingo & Raffle games</li>
                  <li>✓ Ticket selling</li>
                </ul>
              </div>
              
              <Button 
                className="mt-6 w-full bg-amber-500 hover:bg-amber-600"
                onClick={() => navigate(`/dashboard/club/${clubId}/tickets`)}
              >
                Go to Dashboard
              </Button>
            </>
          )}
          
          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-12 h-12 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button 
                onClick={() => navigate(`/dashboard/club/${clubId}/tickets`)}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate(`/dashboard/club/${clubId}/tickets`)}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <p className="text-sm text-slate-500">
                  Need help? Contact <a href="mailto:hi@linedancecentral.uk" className="text-violet-600 hover:underline">hi@linedancecentral.uk</a>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
